import React, { useState, useEffect } from "react";
import { useMsal } from "Auth/msal-context";
import { useNavigate, useLocation } from "react-router";
import KontramLogo from "../../../src/Dashboard/assets/images/images/kontram.png";
import kontramLogoSmall from "../../../src/Dashboard/assets/images/images/kontram_K_tansparent.png";
import { version } from "../../../package.json";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import {
  Avatar,
  Badge,
  Button,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import CustomerListContainer from "../../containers/CustomerListContainer/CustomerListContainer";
import { useSelectedSystems, useSelectedMapSystems, useSelectedSensorSite } from '../../hooks/useCloudSelectors';
import GridViewIcon from "@mui/icons-material/GridView";
import PublicIcon from "@mui/icons-material/Public";
import SensorsIcon from "@mui/icons-material/Sensors";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

const navTabs = () => [
  {
    id: "devices",
    name: "DEVICES",
    value: 0,
    url: "/",
    icon: <GridViewIcon />,
  },
  {
    id: "map",
    name: "MAP",
    value: 1,
    url: "/map",
    icon: <PublicIcon />,
  },
  {
    id: "sensorlist",
    name: "SENSORS",
    value: 2,
    url: "/sensorlist",
    icon: <SensorsIcon />,
  },
  // {
  //   id: "admin",
  //   name: "ADMIN",
  //   value: 3,
  //   url: "/admin",
  //   icon: <AdminPanelSettingsIcon />,
  // },
];

function CloudNavbar() {
  const { isAuthenticated, isAdmin, logout, user } = useMsal();

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isVerySmall = useMediaQuery(theme.breakpoints.down("sm"));
  const selectedSystems = useSelectedSystems();
  const selectedMapSystems = useSelectedMapSystems();
  const selectedSensorSite = useSelectedSensorSite();

  const drawerWidth = isVerySmall ? 280 : 400; // Adjusted width for very small screens

  const tabs = navTabs();

  const handleTabClick = (e, tab) => {
    e.preventDefault();
    navigate(tab.url);
  };

  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerToggle = () => {
    setDrawerOpen((prevState) => !prevState);
  };

  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const drawer = (
    <>
      <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
        <Stack direction="row" alignItems="center" justifyContent="center" sx={{ padding: "5px" }}>
          <img src={KontramLogo} alt="Kontram Logo" height={20} />
        </Stack>
        <Divider />
        <Stack direction="column" alignItems="center" justifyContent="center" spacing={1} sx={{ margin: "4px 16px" }}>
          <Typography sx={{ fontSize: 12 }}>{version}</Typography>
          <Typography>{user?.name}</Typography>
        </Stack>
        <Divider />
        <List>
          {isAuthenticated && (
            <>
              {tabs.map((tab, idx) => {
                if (tab.id === "admin" && isAdmin) {
                  return (
                    <ListItem key={tab.id} disablePadding onClick={(e) => handleTabClick(e, tab)}>
                      <ListItemButton>
                        <ListItemIcon sx={{ mr: 1 }}>{tab.icon}</ListItemIcon>
                        <ListItemText
                          primary={tab.name}
                          sx={{ color: location.pathname === tab.url && theme.palette.primary.main }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                } else if (tab.id !== "admin") {
                  return (
                    <ListItem key={tab.id} disablePadding onClick={(e) => handleTabClick(e, tab)}>
                      <ListItemButton>
                        <ListItemIcon sx={{ mr: 1 }}>{tab.icon}</ListItemIcon>
                        <ListItemText
                          primary={tab.name}
                          sx={{ color: location.pathname === tab.url && theme.palette.primary.main }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                }
              })}
            </>
          )}
        </List>
        <Divider color="primary" />
        <Typography variant="h6" sx={{ padding: "10px" }}>
          Device List
        </Typography>
        <CustomerListContainer
          selectedSystems={location.pathname === "/map" ? selectedMapSystems : selectedSystems}
          selectedSensorSite={selectedSensorSite}
          sensorManagementView={location.pathname === "/sensorlist" ? true : false}
        />
      </Box>
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        component="nav"
        position="static"
        enableColorOnDark
        color={theme.palette.background.paper}
      >
        <Toolbar >
          <Box sx={{ display: "flex", width: "100%", position: "relative", alignItems: "center" }}>
            {/* LEFT SIDE DESKTOP: KONTRAM LOGO, OverSight™, VESION*/}
            <Box sx={{ flex: 2, display: "flex", alignItems: "baseline", display: { xs: "none", lg: "flex" } }}>
              {isMobile ? (
                <img src={kontramLogoSmall} alt="Kontram Logo" height="24px" />
              ) : (
                <img src={KontramLogo} alt="Kontram Logo" height="30px" />
              )}
              <Typography
                fontWeight={600}
                sx={{ ml: 1, color: theme.palette.kontram.primary, fontSize: 22, lineHeight: 1, whiteSpace: "nowrap" }}
              >
                OverSight™ - CONTROL
              </Typography>
              <Typography sx={{ ml: 2, fontSize: 13, color: "white" }}>
                {version}
              </Typography>
            </Box>
            {/* LEFT SIDE MOBILE: NAV MENU TOGGLE */}
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", display: { xs: "flex", lg: "none" } }}>
              <IconButton
                size="large"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleDrawerToggle}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            </Box>
            {/* DEKSTOP CENTER NAV BUTTONS */}
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {isAuthenticated && (
                <Stack direction="row" spacing={1}>
                  {tabs.map((tab, idx) => {
                    if (tab.id === "admin" && isAdmin) {
                      return (
                        <Button
                          key={tab.id}
                          variant={location.pathname === tab.url ? "contained" : "outlined"}
                          onClick={(e) => handleTabClick(e, tab)}
                          startIcon={tab.icon}
                        >
                          {tab.name}
                        </Button>
                      );
                    } else if (tab.id !== "admin") {
                      return (
                        <Button
                          key={tab.id}
                          variant={location.pathname === tab.url ? "contained" : "outlined"}
                          onClick={(e) => handleTabClick(e, tab)}
                          startIcon={tab.icon}
                        >
                          {tab.name}
                        </Button>
                      );
                    }
                  })}
                </Stack>
              )}
            </Box>
            {/* CENTER MOBILE: K small logo, oversight */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "baseline",
                justifyContent: "center",
                display: { xs: "flex", lg: "none" },
              }}
            >
              <img src={kontramLogoSmall} alt="Kontram Logo" height="24px" />
              <Typography fontWeight={600} sx={{ ml: 1, color: theme.palette.kontram.primary, fontSize: 22, lineHeight: 1, whiteSpace: "nowrap" }}>
                OverSight™ - CONTROL
              </Typography>
            </Box>

            {/* RIGHT: BURGER MENU */}
            <Box
              sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
            // onClick={handleOpenUserMenu}
            >
              <Avatar sx={{ bgcolor: theme.palette.primary.main }} onClick={handleOpenUserMenu}>
                {user?.name.charAt(0)}
              </Avatar>
            </Box>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElUser}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              keepMounted
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 250,
                  },
                },
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent={"space-between"} sx={{ margin: "0px 12px" }}>
                <Typography>{user?.name}</Typography>
              </Stack>
              {/* <Divider sx={{ padding: "5px 0px" }} /> */}
              {/* <MenuItem onClick={toggleLanguage} sx={{ justifyContent: "space-between" }} >
              <ListItemIcon sx={{ gap: "15px" }}>
                <LanguageIcon fontSize="small" />
                <Typography>{t("general.language")}</Typography>
              </ListItemIcon>
              <Typography>{i18n.language}</Typography>
            </MenuItem> */}
              {/* <MenuItem onClick={toggleTheme} sx={{ justifyContent: "space-between" }} >
              <ListItemIcon sx={{ gap: "15px" }}>
                {appTheme.theme === "dark" && <DarkModeIcon fontSize="small" />}
                {appTheme.theme === "light" && <LightModeIcon fontSize="small" />}
                <Typography>{t("general.theme")}</Typography>
              </ListItemIcon>
              <Typography>{appTheme.theme.toUpperCase()}</Typography>
            </MenuItem> */}
              {/* <Divider sx={{ paddingTop: "5px" }} /> */}
              <MenuItem onClick={() => logout()}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      {/* NAV DRAWER*/}
      <nav>
        <Drawer
          // container={container}
          variant="temporary"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "block" /*  md: "none" */ },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
      ;
    </Box >
  );
}

export default CloudNavbar;

// <Navbar expand="lg" className="main-navbar ">
//   <Container fluid>
//     <Navbar.Brand style={{ display: "flex"}} >
//       <img src={KontramLogo} alt="Kontram Logo" height="35px" />
//       <h1 className="ms-2" style={{ color: "#009a4b", marginTop:0, fontSize:35, fontFamily: "Kanit SemiBold"}}>OverSight™</h1>
//       <h2 className="ms-2" style={{ color: "white", marginTop:21, fontSize: 14 }}>{version}</h2>
//       </Navbar.Brand >
//     <Navbar.Toggle aria-controls="main-navbar" />
//     <Navbar.Collapse id="main-navbar">
//       {isAuthenticated && (
//         <>
//           <Nav className="mx-auto">
//             {tabs.map((tab, idx) => {
//               if (tab.id === "admin" && isAdmin) {
//                 return (
//                   <Nav.Item className="mx-1 my-1" key={tab.id} >
//                     <Nav.Link
//                       className={clsx(
//                         "nav-link tab-link",
//                         location.pathname === tab.url && "active",
//                         idx === 0 && "first",
//                         idx === 1 && "second",
//                         idx === 2 && "last"
//                       )}
//                       onClick={(e) => handleTabClick(e, tab)}
//                     >
//                       {tab.name}
//                     </Nav.Link>
//                   </Nav.Item>
//                 );
//               } if (tab.id !== "admin") {
//                 return (
//                   <Nav.Item className="mx-1 my-1" key={tab.id} >
//                     <Nav.Link
//                       className={clsx(
//                         "nav-link tab-link",
//                         location.pathname === tab.url && "active",
//                         idx === 0 && "first",
//                         idx === 1 && "second",
//                         idx === 2 && "last"
//                       )}
//                       onClick={(e) => handleTabClick(e, tab)}
//                     >
//                       {tab.name}
//                     </Nav.Link>
//                   </Nav.Item >
//                 );
//               }
//             })}
//           </Nav>
//           <Nav className="ms-3 custom-dropdown-menu" >
//             <NavDropdown className="px-4 mt-2" title={user?.name} id="nav-dropdown" >
//               <NavDropdown.Item onClick={() => { logout(); }} >
//                 <i className="icon-custom-logout" />
//                 <span className="logout-text" > Logout </span>
//               </NavDropdown.Item>
//             </NavDropdown>
//           </Nav>
//         </>
//       )}
//     </Navbar.Collapse>
//   </Container>
// </Navbar>
